<template>
    <div class="violation-page">
        <div class="summary-grid">
            <div class="summary-card danger">
                <div class="summary-label">风险账户数</div>
                <div class="summary-value">{{ summary.impactedAdvertiserCount || 0 }}</div>
                <div class="summary-desc">存在生效中违规记录的账户</div>
            </div>
            <div class="summary-card warning">
                <div class="summary-label">待处理事件数</div>
                <div class="summary-value">{{ summary.activeViolationCount || 0 }}</div>
                <div class="summary-desc">当前仍需关注的违规事件</div>
            </div>
            <div class="summary-card normal">
                <div class="summary-label">已扫描账户数</div>
                <div class="summary-value">{{ summary.checkedAccountCount || 0 }}</div>
                <div class="summary-desc">已扫描 {{ summary.checkedAccountCount || 0 }} 个账户</div>
            </div>
        </div>

        <div class="toolbar">
            <div class="toolbar-left">
                <el-select
                    v-model="searchForm.groupId"
                    clearable
                    filterable
                    placeholder="账户分组"
                    class="toolbar-input"
                    @change="handleSearch"
                >
                    <el-option
                        v-for="item in advertiserGroupOptions"
                        :key="item.id"
                        :label="item.name"
                        :value="item.id"
                    />
                </el-select>
                <el-input
                    v-model.trim="searchForm.name"
                    placeholder="账户名称 / 公司名称 / 广告账户ID"
                    clearable
                    class="toolbar-input"
                    @keyup.enter.native="handleSearch"
                />
                <el-input
                    v-model.trim="searchForm.advertiserId"
                    placeholder="广告账户ID"
                    clearable
                    class="toolbar-input id-input"
                    @keyup.enter.native="handleSearch"
                />
                <el-button type="primary" @click="handleSearch">搜索</el-button>
                <el-button @click="handleReset">重置</el-button>
            </div>
            <div class="toolbar-right">
                <span class="fetch-tip" v-if="pendingCount">当前页 {{ pendingCount }} 个账户数据尚未就绪</span>
                <span class="fetch-tip" v-if="summary.fetchFailedCount">{{ summary.fetchFailedCount }} 个账户加载失败</span>
                <el-button type="danger" plain @click="refreshAll">刷新</el-button>
            </div>
        </div>

        <div class="table-wrap">
            <div v-if="showTableHint" class="table-hint" :class="tableHintType">
                {{ tableHintText }}
            </div>
            <el-table
                :data="tableData"
                border
                stripe
                v-loading="loading"
                :empty-text="tableEmptyText"
                style="width: 100%"
            >
                <el-table-column prop="advertiserId" label="广告账户ID" min-width="150" />
                <el-table-column prop="name" label="账户名称" min-width="180" />
                <el-table-column prop="company" label="公司名称" min-width="220" show-overflow-tooltip />
                <el-table-column label="风险状态" width="120">
                    <template slot-scope="{ row }">
                        <el-tag :type="riskTagType(row)">
                            {{ riskLabel(row) }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="生效中数量" width="110">
                    <template slot-scope="{ row }">
                        {{ formatCount(row.activeViolationCount, row.fetchStatus) }}
                    </template>
                </el-table-column>
                <el-table-column label="最近扣分" width="110">
                    <template slot-scope="{ row }">
                        {{ formatNumber(row.latestScore, row.fetchStatus) }}
                    </template>
                </el-table-column>
                <el-table-column label="最近时间" min-width="170">
                    <template slot-scope="{ row }">
                        {{ formatText(row.latestViolationTime, row.fetchStatus) }}
                    </template>
                </el-table-column>
                <el-table-column label="最近状态" width="110">
                    <template slot-scope="{ row }">
                        <el-tag size="mini" :type="statusTagType(row.latestStatus, row.fetchStatus)">
                            {{ formatStatus(row.latestStatus, row.fetchStatus) }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="最近违规类型" min-width="140" show-overflow-tooltip>
                    <template slot-scope="{ row }">
                        {{ formatText(row.latestIllegalType, row.fetchStatus) }}
                    </template>
                </el-table-column>
                <el-table-column label="最近违规原因" min-width="320" show-overflow-tooltip>
                    <template slot-scope="{ row }">
                        {{ formatText(row.latestRejectReason, row.fetchStatus) }}
                    </template>
                </el-table-column>
                <el-table-column label="拉取状态" min-width="200" show-overflow-tooltip>
                    <template slot-scope="{ row }">
                        <div class="fetch-status-cell">
                            <el-tag size="mini" :type="fetchStatusTagType(row.fetchStatus)">
                                {{ fetchStatusLabel(row.fetchStatus) }}
                            </el-tag>
                            <div v-if="row.fetchStatus === 'FAILED' && row.fetchError" class="fetch-status-error">
                                {{ row.fetchError }}
                            </div>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="120" fixed="right">
                    <template slot-scope="{ row }">
                        <el-button type="text" @click="goDetail(row)">详情</el-button>
                    </template>
                </el-table-column>
            </el-table>

            <div class="pagination-wrap">
                <el-pagination
                    background
                    layout="total, sizes, prev, pager, next, jumper"
                    :current-page="page.pageIndex"
                    :page-size="page.pageSize"
                    :page-sizes="[10, 20, 50, 100]"
                    :total="page.total"
                    @current-change="handleCurrentChange"
                    @size-change="handleSizeChange"
                />
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'violationPointsRecord',
    data() {
        return {
            loading: false,
            pendingRefreshTimer: null,
            tableRequestId: 0,
            summary: {
                checkedAccountCount: 0,
                totalAccountCount: 0,
                impactedAdvertiserCount: 0,
                activeViolationCount: 0,
                fetchFailedCount: 0,
                lastSyncTime: ''
            },
            advertiserGroupOptions: [],
            searchForm: {
                groupId: undefined,
                name: '',
                advertiserId: ''
            },
            tableData: [],
            tableLoadError: false,
            page: {
                pageIndex: 1,
                pageSize: 10,
                total: 0
            }
        }
    },
    computed: {
        hasFilters() {
            return !!(this.searchForm.groupId || this.searchForm.name || this.searchForm.advertiserId)
        },
        pendingCount() {
            return this.tableData.filter(item => item.fetchStatus === 'PENDING').length
        },
        hasPendingRows() {
            return this.pendingCount > 0
        },
        tableEmptyText() {
            if (this.tableLoadError) {
                return '加载失败，请点击刷新重试。'
            }
            if (this.hasPendingRows && this.page.total === 0) {
                return '部分账户数据尚未就绪，请稍后刷新。'
            }
            if (this.hasFilters) {
                return '当前筛选条件下暂无数据。'
            }
            if ((this.summary.totalAccountCount || 0) === 0) {
                return '当前登录账号没有可见的千川广告账户。'
            }
            if ((this.summary.fetchFailedCount || 0) > 0 && (this.summary.checkedAccountCount || 0) === 0) {
                return '全部账户拉取失败，请检查授权后重试。'
            }
            return '暂无数据。'
        },
        showTableHint() {
            if (this.loading) {
                return false
            }
            return this.tableLoadError || this.hasPendingRows || this.page.total === 0
        },
        tableHintType() {
            if (this.tableLoadError) {
                return 'is-error'
            }
            return this.hasPendingRows ? 'is-info' : 'is-warning'
        },
        tableHintText() {
            if (this.tableLoadError) {
                return '违规记录加载失败，可能是后端超时或接口暂不可用。'
            }
            if (this.hasPendingRows) {
                return `当前页有 ${this.pendingCount} 个账户数据尚未就绪，请稍后刷新或点击右上角刷新。`
            }
            if (this.hasFilters) {
                return '当前筛选条件下没有匹配记录，请尝试其他关键词或广告账户ID。'
            }
            if ((this.summary.totalAccountCount || 0) === 0) {
                return '当前登录账号没有可见的千川广告账户，因此列表为空。'
            }
            if ((this.summary.fetchFailedCount || 0) > 0 && (this.summary.checkedAccountCount || 0) === 0) {
                return '存在可见账户，但本轮全部拉取失败，请检查授权后再刷新。'
            }
            return '当前暂无违规记录。'
        }
    },
    created() {
        this.loadAdvertiserGroupOptions()
        this.fetchTableData()
        this.$nextTick(() => {
            this.fetchSummary()
        })
    },
    beforeDestroy() {
        this.stopPendingRefresh()
    },
    methods: {
        loadAdvertiserGroupOptions() {
            this.$axios.post({
                url: '/oceanEngineAdvertiserGroup/getAll',
                loading: false,
                success: (res) => {
                    if (res.code === 0) {
                        this.advertiserGroupOptions = res.data || []
                    }
                }
            })
        },
        fetchSummary() {
            this.$axios.post({
                url: '/qianchuanViolationMonitor/summary',
                data: {
                    groupId: this.searchForm.groupId || undefined
                },
                loading: false,
                success: (res) => {
                    if (res.code === 0 && res.data) {
                        this.summary = Object.assign({}, this.summary, this.pickMonitorSummary(res.data))
                    }
                }
            })
        },
        pickMonitorSummary(data) {
            return {
                totalAccountCount: data.totalAccountCount,
                checkedAccountCount: data.checkedAccountCount,
                impactedAdvertiserCount: data.impactedAdvertiserCount,
                activeViolationCount: data.activeViolationCount,
                fetchFailedCount: data.fetchFailedCount,
                lastSyncTime: data.lastSyncTime
            }
        },
        fetchTableData(options = {}) {
            const opts = Object.assign({
                showLoading: true
            }, options)
            const requestId = ++this.tableRequestId
            if (opts.showLoading) {
                this.loading = true
            }
            this.tableLoadError = false
            const advertiserId = this.searchForm.advertiserId ? Number(this.searchForm.advertiserId) : undefined
            this.$axios.post({
                url: '/qianchuanViolationMonitor/overview',
                data: {
                    groupId: this.searchForm.groupId || undefined,
                    advertiserId,
                    name: this.searchForm.name || undefined,
                    pageIndex: this.page.pageIndex,
                    pageSize: this.page.pageSize
                },
                success: (res) => {
                    if (requestId !== this.tableRequestId) {
                        return
                    }
                    this.loading = false
                    const loadFailed = res.code !== 0 || !res.data
                    if (loadFailed) {
                        this.tableLoadError = true
                        this.tableData = []
                        this.page.total = 0
                        this.stopPendingRefresh()
                    }
                    if (loadFailed) {
                        this.$message.error(res.msg || '加载风险账户失败')
                        return
                    }
                    this.tableData = res.data.content || []
                    this.page.total = res.data.totalElements || 0
                    this.syncPendingRefreshState()
                },
                error: () => {
                    if (requestId !== this.tableRequestId) {
                        return
                    }
                    this.tableLoadError = true
                    this.tableData = []
                    this.page.total = 0
                    this.loading = false
                    this.stopPendingRefresh()
                    this.$message.error('加载风险账户失败')
                }
            })
        },
        startPendingRefresh() {
            if (this.pendingRefreshTimer || !this.hasPendingRows) {
                return
            }
            this.pendingRefreshTimer = setInterval(() => {
                this.fetchTableData({ showLoading: false })
                this.fetchSummary()
            }, 120000)
        },
        stopPendingRefresh() {
            if (this.pendingRefreshTimer) {
                clearInterval(this.pendingRefreshTimer)
                this.pendingRefreshTimer = null
            }
        },
        syncPendingRefreshState() {
            if (this.hasPendingRows) {
                this.startPendingRefresh()
                return
            }
            this.stopPendingRefresh()
        },
        handleSearch() {
            this.stopPendingRefresh()
            this.page.pageIndex = 1
            this.fetchSummary()
            this.fetchTableData()
        },
        handleReset() {
            this.stopPendingRefresh()
            this.searchForm = {
                groupId: undefined,
                name: '',
                advertiserId: ''
            }
            this.page.pageIndex = 1
            this.fetchSummary()
            this.fetchTableData()
        },
        refreshAll() {
            this.stopPendingRefresh()
            this.fetchTableData()
            this.fetchSummary()
        },
        handleCurrentChange(pageIndex) {
            this.stopPendingRefresh()
            this.page.pageIndex = pageIndex
            this.fetchTableData()
        },
        handleSizeChange(pageSize) {
            this.stopPendingRefresh()
            this.page.pageSize = pageSize
            this.page.pageIndex = 1
            this.fetchTableData()
        },
        goDetail(row) {
            this.$router.push({
                path: '/qdd/admin/violationRecordDetails.html',
                query: {
                    advertiserId: row.advertiserId,
                    name: row.name
                }
            })
        },
        formatStatus(status, fetchStatus) {
            if (fetchStatus === 'PENDING') return '加载中'
            if (fetchStatus === 'FAILED') return '加载失败'
            if (!status) return '--'
            const map = {
                VALID: '生效中',
                INVALID: '已失效',
                DISPOSAL: '已处置'
            }
            return map[status] || status
        },
        statusTagType(status, fetchStatus) {
            if (fetchStatus === 'PENDING') return 'info'
            if (fetchStatus === 'FAILED') return 'danger'
            if (status === 'VALID') return 'danger'
            if (status === 'INVALID') return 'info'
            return ''
        },
        fetchStatusLabel(status) {
            if (status === 'PENDING') return '加载中'
            if (status === 'FAILED') return '加载失败'
            return '正常'
        },
        fetchStatusTagType(status) {
            if (status === 'PENDING') return 'info'
            if (status === 'FAILED') return 'danger'
            return 'success'
        },
        riskLabel(row) {
            if (!row || row.fetchStatus === 'PENDING') return '加载中'
            if (row.fetchStatus === 'FAILED') return '加载失败'
            return row.hasRisk ? '有风险' : '正常'
        },
        riskTagType(row) {
            if (!row || row.fetchStatus === 'PENDING') return 'info'
            if (row.fetchStatus === 'FAILED') return 'danger'
            return row.hasRisk ? 'danger' : 'success'
        },
        formatNumber(value, fetchStatus) {
            if (fetchStatus === 'PENDING' || fetchStatus === 'FAILED') {
                return '--'
            }
            if (value === null || value === undefined || value === '') {
                return '--'
            }
            return Number(value).toFixed(2)
        },
        formatCount(value, fetchStatus) {
            if (fetchStatus === 'PENDING' || fetchStatus === 'FAILED') {
                return '--'
            }
            if (value === null || value === undefined || value === '') {
                return '--'
            }
            return value
        },
        formatText(value, fetchStatus) {
            if (fetchStatus === 'PENDING') return '加载中'
            if (fetchStatus === 'FAILED') return '加载失败'
            return value || '--'
        }
    }
}
</script>

<style scoped>
.violation-page {
    padding: 24px;
    background: #f5f7fb;
    min-height: calc(100vh - 48px);
}

.summary-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    margin-bottom: 18px;
}

.summary-card {
    background: #fff;
    border-radius: 16px;
    padding: 20px 22px;
    box-shadow: 0 10px 30px rgba(15, 35, 95, 0.06);
}

.summary-card.danger {
    border-top: 4px solid #f56c6c;
}

.summary-card.warning {
    border-top: 4px solid #e6a23c;
}

.summary-card.normal {
    border-top: 4px solid #409eff;
}

.summary-label {
    font-size: 14px;
    color: #7b8190;
}

.summary-value {
    margin-top: 12px;
    font-size: 32px;
    font-weight: 700;
    color: #1f2a44;
}

.summary-desc {
    margin-top: 10px;
    font-size: 13px;
    color: #909399;
}

.toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
    background: #fff;
    border-radius: 16px;
    padding: 16px 18px;
}

.toolbar-left,
.toolbar-right {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
}

.toolbar-input {
    width: 280px;
}

.id-input {
    width: 180px;
}

.fetch-tip {
    color: #e6a23c;
    font-size: 13px;
}

.table-wrap {
    background: #fff;
    border-radius: 16px;
    padding: 16px 16px 8px;
    box-shadow: 0 10px 30px rgba(15, 35, 95, 0.06);
}

.table-hint {
    margin-bottom: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    font-size: 13px;
    line-height: 1.5;
}

.table-hint.is-warning {
    color: #8a6214;
    background: #fff7e6;
    border: 1px solid #f3d19e;
}

.table-hint.is-info {
    color: #2f5f9b;
    background: #eef5ff;
    border: 1px solid #bcd7ff;
}

.table-hint.is-error {
    color: #c45656;
    background: #fef0f0;
    border: 1px solid #fbc4c4;
}

.pagination-wrap {
    padding: 18px 0 10px;
    display: flex;
    justify-content: flex-end;
}

.fetch-status-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
}

.fetch-status-error {
    font-size: 12px;
    line-height: 1.4;
    color: #f56c6c;
    white-space: normal;
    word-break: break-all;
}

@media (max-width: 1024px) {
    .summary-grid {
        grid-template-columns: 1fr;
    }

    .toolbar {
        align-items: flex-start;
    }

    .toolbar-input,
    .id-input {
        width: 100%;
    }
}
</style>
